import { useState, useEffect } from 'react'
import { useGameStore } from '@store/gameStore'
import { GAME_CONFIG } from '@config/gameConfig'
import { GameLayout } from '@components/GameLayout'
import './BattlePassScreen.css'

interface Reward {
    type: string
    amount: number
    emoji: string
    label?: string
}

const generateRewards = () => {
    const levels = []
    const MAX_LEVEL = 50
    const EQUIPMENT_EMOJIS = ['⚔️', '🛡️', '🪖', '💍', '👢']

    for (let level = 1; level <= MAX_LEVEL; level++) {
        // FREE REWARDS
        let freeReward: Reward = { type: 'gold', amount: level * 100, emoji: '🪙' }
        if (level % 5 === 0) freeReward = { type: 'gems', amount: level * 5, emoji: '💎' }
        if (level % 10 === 0) freeReward = {
            type: 'item',
            amount: 1,
            emoji: EQUIPMENT_EMOJIS[level % EQUIPMENT_EMOJIS.length],
            label: 'Rare Item'
        }

        // PREMIUM REWARDS
        let premiumReward: Reward = { type: 'gold', amount: level * 500, emoji: '🪙' }
        if (level % 2 === 0) premiumReward = { type: 'gems', amount: level * 20, emoji: '💎' }

        if (level % 5 === 0) {
            premiumReward = { type: 'energy', amount: 50 + level, emoji: '⚡', label: 'Energie' }
        }

        if (level % 10 === 0) {
            premiumReward = {
                type: 'legendary',
                amount: 1,
                emoji: '🌟',
                label: `Legendär!`
            }
        }

        if (level === MAX_LEVEL) {
            premiumReward = { type: 'exclusive', amount: 1, emoji: '👑', label: 'König Skin' }
        }

        levels.push({ level, freeReward, premiumReward })
    }
    return levels
}

const REWARDS = generateRewards()

export function BattlePassScreen() {
    const currentXP = useGameStore(state => state.battlePassXP)
    const isPremium = useGameStore(state => state.battlePassPremium)
    const claimedFree = useGameStore(state => state.claimedBattlePassFree)
    const claimedPremium = useGameStore(state => state.claimedBattlePassPremium)
    const totalGameMinutes = useGameStore(state => state.totalGameMinutes)

    // Season Logic
    const SEASON_DURATION_MINUTES = 90 * 24 * 60
    const currentSeasonMinute = totalGameMinutes % SEASON_DURATION_MINUTES
    const minutesUntilEnd = SEASON_DURATION_MINUTES - currentSeasonMinute

    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [showFundsModal, setShowFundsModal] = useState(false)

    const spendEuro = useGameStore(state => state.spendEuro)
    const spendGems = useGameStore(state => state.spendGems)
    const trackMoneySpent = useGameStore(state => state.trackMoneySpent)
    const setScreen = useGameStore(state => state.setScreen)

    const buyBattlePassPremium = useGameStore(state => state.buyBattlePassPremium)
    const buyBattlePassLevels = useGameStore(state => state.buyBattlePassLevels)
    const claimBattlePassReward = useGameStore(state => state.claimBattlePassReward)

    const XP_PER_LEVEL = 1000
    const currentLevel = Math.floor(currentXP / XP_PER_LEVEL) + 1
    const xpInLevel = currentXP % XP_PER_LEVEL
    const progressPct = (xpInLevel / XP_PER_LEVEL) * 100
    const MAX_LEVEL = 50

    useEffect(() => {
        // Redraw timer every minute or so if needed
        const t = setInterval(() => { /* force update for timer? */ }, 60000)
        return () => clearInterval(t)
    }, [])

    const formatTime = (minutes: number) => {
        const d = Math.floor(minutes / (24 * 60))
        const h = Math.floor((minutes % (24 * 60)) / 60)
        return `${d}T ${h}Std`
    }

    const handleBuyPremium = () => {
        const price = GAME_CONFIG.pricing.battlePassPremium
        if (spendEuro(price)) {
            trackMoneySpent(price)
            buyBattlePassPremium()
            setShowSuccessModal(true)
        } else {
            setShowFundsModal(true)
        }
    }

    const handleBuyLevels = (n: number) => {
        if (spendGems(n * GAME_CONFIG.pricing.battlePassLevelCost)) {
            buyBattlePassLevels(n)
        }
    }

    const claimFree = (lvl: number) => {
        if (lvl > currentLevel || claimedFree.includes(lvl)) return
        const r = REWARDS[lvl - 1].freeReward
        claimBattlePassReward(lvl, 'free', r)
    }

    const claimPremium = (lvl: number) => {
        if (lvl > currentLevel || claimedPremium.includes(lvl) || !isPremium) return
        const r = REWARDS[lvl - 1].premiumReward
        claimBattlePassReward(lvl, 'premium', r)
    }

    return (
        <GameLayout>
            <div className="bp">
                {/* Header */}
                <div className="bp__header">
                    <button className="bp__back" onClick={() => setScreen('city')}>← Zurück</button>
                    <div className="bp__title">
                        <h1>SEASON 1</h1>
                        <span>Legends of Greed</span>
                    </div>
                </div>

                {/* Timer */}
                <div className="bp__timer-bar">
                    <span>👑 Season endet in <strong>{formatTime(minutesUntilEnd)}</strong></span>
                </div>

                {/* Progress Hero */}
                <div className="bp__progress-hero">
                    <div className="bp__level-display">
                        <span className="bp__level-num">{Math.min(currentLevel, MAX_LEVEL)}</span>
                        <span className="bp__level-label">/ {MAX_LEVEL}</span>
                    </div>
                    <div className="bp__xp-section">
                        <div className="bp__xp-bar">
                            <div className="bp__xp-fill" style={{ width: `${progressPct}%` }} />
                        </div>
                        <span className="bp__xp-text">{xpInLevel} / {XP_PER_LEVEL} XP</span>
                    </div>
                </div>

                {/* Premium CTA */}
                {!isPremium ? (
                    <button className="bp__buy-premium-3d" onClick={handleBuyPremium}>
                        <div className="bp__buy-content">
                            <div className="bp__buy-icon">✨</div>
                            <div className="bp__buy-info">
                                <span className="bp__buy-title">Premium Pass aktivieren</span>
                                <span className="bp__buy-sub">Schalte alle 50 Belohnungen frei!</span>
                            </div>
                        </div>
                        <div className="bp__buy-price-tag">
                            {GAME_CONFIG.pricing.battlePassPremium.toFixed(2)}€
                        </div>
                    </button>
                ) : (
                    <div className="bp__premium-active">
                        <span>👑</span> PREMIUM PASS AKTIV
                    </div>
                )}

                {/* Level Purchase */}
                <div className="bp__level-buy">
                    <button className="bp__level-btn" onClick={() => handleBuyLevels(1)}>
                        <span>⚡ LEVEL-UP</span>
                        <div className="bp__level-cost">{GAME_CONFIG.pricing.battlePassLevelCost} 💎</div>
                    </button>
                    <button className="bp__level-btn bp__level-btn--big" onClick={() => handleBuyLevels(10)}>
                        <span>🚀 +10 LEVELS</span>
                        <div className="bp__level-cost">{GAME_CONFIG.pricing.battlePassLevelCost * 10} 💎</div>
                    </button>
                </div>

                {/* Rewards List */}
                <div className="bp__rewards-section">
                    <div className="bp__track-header">
                        <span>FREE</span>
                        <span>LVL</span>
                        <span>PREMIUM</span>
                    </div>

                    <div className="bp__rewards-scroll">
                        {REWARDS.map(({ level, freeReward, premiumReward }) => {
                            const unlocked = level <= currentLevel
                            const freeClaimed = claimedFree.includes(level)
                            const premClaimed = claimedPremium.includes(level)
                            const freeClaimable = unlocked && !freeClaimed
                            const premClaimable = unlocked && isPremium && !premClaimed

                            return (
                                <div key={level} className="bp__reward-row">
                                    {/* Free */}
                                    <div
                                        className={`bp__reward bp__reward--free ${unlocked ? 'bp__reward--unlocked' : ''} ${freeClaimed ? 'bp__reward--claimed' : ''}`}
                                        onClick={() => claimFree(level)}
                                    >
                                        <span className="bp__reward-emoji">{freeReward.emoji}</span>
                                        <span className={`bp__reward-amt ${freeReward.type === 'gold' ? 'bp__reward-amt--gold' : ''}`}>
                                            {freeReward.label || freeReward.amount}
                                        </span>
                                        {freeClaimable && <span className="bp__reward-badge">!</span>}
                                        {freeClaimed && <span className="bp__reward-check">✓</span>}
                                    </div>

                                    {/* Level Marker */}
                                    <div className={`bp__level-marker ${unlocked ? 'bp__level-marker--unlocked' : ''}`}>
                                        {level}
                                    </div>

                                    {/* Premium */}
                                    <div
                                        className={`bp__reward bp__reward--premium ${unlocked && isPremium ? 'bp__reward--unlocked' : ''} ${premClaimed ? 'bp__reward--claimed' : ''} ${!isPremium ? 'bp__reward--locked' : ''}`}
                                        onClick={() => claimPremium(level)}
                                    >
                                        <span className="bp__reward-emoji">{premiumReward.emoji}</span>
                                        <span className={`bp__reward-amt ${premiumReward.type === 'gold' ? 'bp__reward-amt--gold' : ''}`}>
                                            {premiumReward.label || premiumReward.amount}
                                        </span>

                                        {/* Lock Icon for Non-Premium */}
                                        {!isPremium && <span className="bp__reward-lock">🔒</span>}

                                        {premClaimable && <span className="bp__reward-badge">!</span>}
                                        {premClaimed && <span className="bp__reward-check">✓</span>}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
                    <div className="modal modal--premium-success" onClick={e => e.stopPropagation()}>
                        <div className="modal__header">
                            <h2 style={{ color: '#FFD700' }}>👑 WILLKOMMEN IM CLUB!</h2>
                        </div>
                        <div className="modal__content">
                            <div className="bp__success-icon">✨</div>
                            <p>Du bist jetzt stolzer Besitzer des <strong>Premium Pass</strong>.</p>
                            <ul className="bp__success-list">
                                <li>✅ 50x Premium Belohnungen freigeschaltet</li>
                                <li>✅ Exklusive Profil-Effekte aktiviert</li>
                                <li>✅ Deine Gegner werden vor Neid erblassen</li>
                            </ul>
                        </div>
                        <div className="modal__actions">
                            <button className="btn btn--gold btn--full" onClick={() => setShowSuccessModal(false)}>
                                CLAIM IT ALL!
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Funds Modal */}
            {showFundsModal && (
                <div className="modal-overlay" onClick={() => setShowFundsModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal__header">
                            <h2>❌ ZU WENIG GELD</h2>
                        </div>
                        <div className="modal__content">
                            <p>Dein Bankkonto deckt diesen Luxus nicht ab. Besuch die Bank oder frag Oma!</p>
                        </div>
                        <div className="modal__actions">
                            <button className="btn btn--primary btn--full" onClick={() => { setShowFundsModal(false); setScreen('bank'); }}>
                                🏦 Zur Bank
                            </button>
                            <button className="btn btn--ghost btn--full" onClick={() => setShowFundsModal(false)}>
                                Schließen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </GameLayout>
    )
}
